import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react'
import axios from 'axios'

// API Base URL - adjust according to your backend
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export interface User {
  customerId: string
  email: string
  shopName?: string
  mobile?: string
  // Package fields (migrated from package_level to current_package_id)
  packageId?: string           // UUID from credit_packages.id
  packageCode: string          // Package code (starter, basic, pro, etc.)
  packageName?: string         // Display name from credit_packages.name
  packageLevel: string         // Backward compatibility (deprecated)
  credits?: number
  // RBAC fields
  roleCode: string
  roleName?: string
  permissions: string[]
  isActive: boolean
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (identifier: string, password: string) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
  refreshCredits: () => Promise<void>
  registerDisplayCreditsCallback: (callback: (credits: number) => void) => void
  updateDisplayCredits: (credits: number) => void
  hasPermission: (permission: string) => boolean
  hasPermissions: (permissions: string[], requireAll?: boolean) => boolean
  hasRole: (roles: string | string[]) => boolean
  checkAccess: (config: {
    permissions?: string[]
    permissionsAll?: string[]
    roles?: string[]
  }) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const displayCreditsCallbackRef = useRef<((credits: number) => void) | null>(null)

  // Load user from token on mount
  useEffect(() => {
    // Check for both authToken (login) and accessToken (registration)
    const token = localStorage.getItem('authToken') || localStorage.getItem('accessToken')
    if (token) {
      loadUserFromToken(token)
    } else {
      setLoading(false)
    }
  }, [])

  // Configure axios defaults only when token changes (not when user updates)
  useEffect(() => {
    // Check for both authToken (login) and accessToken (registration)
    const token = localStorage.getItem('authToken') || localStorage.getItem('accessToken')
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete axios.defaults.headers.common['Authorization']
    }
  }, []) // Empty dependency to run only on mount

  const loadUserFromToken = async (token: string) => {
    try {
      // First, try to get user data from localStorage (stored during login)
      const storedUserData = localStorage.getItem('user')
      let userData = storedUserData ? JSON.parse(storedUserData) : null
      
      // Decode JWT to get user data directly
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      const decoded = JSON.parse(jsonPayload)
      
      // Check if token is expired
      if (decoded.exp && decoded.exp * 1000 < Date.now()) {
        console.log('🔐 Token expired, clearing authentication')
        localStorage.removeItem('authToken')
        localStorage.removeItem('accessToken')
        localStorage.removeItem('user')
        delete axios.defaults.headers.common['Authorization']
        setUser(null)
        setLoading(false)
        return
      }
      
      // Merge data from localStorage and JWT token (localStorage takes priority for display data)
      const apiPermissions = decoded.permissions || userData?.permissions || [];
      setUser({
        customerId: decoded.id || decoded.customerId || userData?.id,
        email: decoded.email || userData?.email,
        shopName: userData?.shopName || decoded.shopName || 'ร้านของฉัน',
        mobile: decoded.mobile || userData?.phoneNumber,
        // New package fields from migrated authentication
        packageId: decoded.packageId || userData?.packageId,
        packageCode: decoded.packageCode || userData?.packageType || decoded.packageLevel || 'starter',
        packageName: decoded.packageName || userData?.packageName,
        packageLevel: decoded.packageCode || userData?.packageType || decoded.packageLevel || 'starter', // Backward compat
        credits: userData?.creditBalance ?? decoded.credits ?? 0,
        roleCode: decoded.role_code || decoded.roleCode || userData?.role_code || 'customer_user',
        permissions: (apiPermissions.length > 0) ? apiPermissions : [
          'slip:view',
          'slip:verify',
          'dashboard:view',
          'billing:view',    // ⚠️ TEMPORARY FIX for Issue #2
          'api:view',
          'api:use',
          'reports:view'
        ],
        isActive: decoded.isActive !== false
      })
      
      console.log('🔐 User loaded from token:', {
        customerId: decoded.id || decoded.customerId || userData?.id,
        shopName: userData?.shopName || decoded.shopName,
        credits: userData?.creditBalance ?? decoded.credits ?? 0,
        creditBalance: userData?.creditBalance,
        decodedCredits: decoded.credits,
        permissions: (decoded.permissions || []).length
      })
    } catch (error) {
      console.error('Failed to load user from token:', error)
      localStorage.removeItem('authToken')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('user')
      delete axios.defaults.headers.common['Authorization']
    } finally {
      setLoading(false)
    }
  }

  const login = async (identifier: string, password: string) => {
    try {
      // Detect if identifier is email or mobile
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)
      const isMobile = /^0[6-9]\d{8}$/.test(identifier)
      
      if (!isEmail && !isMobile) {
        throw new Error('รูปแบบอีเมลหรือเบอร์โทรศัพท์ไม่ถูกต้อง')
      }

      // Use unified login endpoint that accepts both email and mobile
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        ...(isEmail ? { email: identifier } : { mobile: identifier }),
        password
      })

      const { token, user, customer, role } = response.data.data
      
      // Support both customer (owner) and user (team member) response formats
      const userData = user || customer
      
      if (!userData) {
        throw new Error('Invalid login response - no user data')
      }
      
      console.log('📦 API Login Response - userData:', {
        id: userData.id,
        shop_name: userData.shop_name,
        credits: userData.credits,
        credit_balance: userData.credit_balance,
        allKeys: Object.keys(userData)
      })
      
      // Store token
      localStorage.setItem('authToken', token)
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      
      // Store user data with KYC info for login page redirect check
      const userDataForStorage = {
        id: userData.id,
        email: userData.email,
        phoneNumber: userData.mobile,
        shopName: userData.shop_name || userData.name,
        packageType: userData.package_level || userData.packageLevel || 'starter',
        creditBalance: userData.credits || 0,
        requiresKyc: userData.requiresKyc ?? false,
        kycVerified: userData.kycVerified ?? false,
        kycType: userData.kyc_type || null,
        accountType: userData.accountType || 'customer',
        userType: userData.userType || userData.user_type || 'owner',
        business_id: userData.business_id || null
      }
      localStorage.setItem('user', JSON.stringify(userDataForStorage))
      
      // Store business data for easy access by API functions
      if (userData.business_id) {
        const businessData = {
          id: userData.business_id,
          name: userData.shop_name || userData.name,
          customer_id: userData.id
        }
        localStorage.setItem('businessData', JSON.stringify(businessData))
      }
      
      // Set user data directly from login response
      setUser({
        customerId: userData.id,
        email: userData.email,
        shopName: userData.shop_name || userData.name,
        mobile: userData.mobile,
        // New package fields from migrated authentication
        packageId: userData.current_package_id || userData.packageId,
        packageCode: userData.package_code || userData.package_level || 'starter',
        packageName: userData.package_name || userData.packageName,
        packageLevel: userData.package_code || userData.package_level || 'starter', // Backward compat
        credits: userData.credits || 0,
        roleCode: userData.role_code || role?.role_code || 'customer_user',
        permissions: (userData.permissions && userData.permissions.length > 0) ? userData.permissions : [
          'slip:view', 
          'slip:verify', 
          'dashboard:view',
          'billing:view',    // ⚠️ TEMPORARY FIX for Issue #2 - Backend not sending billing permission
          'api:view',
          'api:use',
          'reports:view'
        ],
        isActive: true
      })
      
      console.log('🔐 User logged in:', {
        accountType: userData.accountType,
        userType: userData.userType || userData.user_type,
        roleCode: userData.role_code || role?.role_code,
        credits: userData.credits || 0,
        permissionsCount: userData.permissions?.length || 0,
        firstFewPermissions: userData.permissions?.slice(0, 5)
      })
    } catch (error) {
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('user')
    delete axios.defaults.headers.common['Authorization']
    setUser(null)
  }

  const refreshUser = async () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('accessToken')
    if (token) {
      await loadUserFromToken(token)
    }
  }

  // Refresh credit balance only (lightweight)
  const refreshCredits = useCallback(async () => {
    // Check token directly instead of user state — user may not be set yet on page refresh
    const token = localStorage.getItem('authToken') || localStorage.getItem('accessToken')
    if (!token) return

    console.log('💰 refreshCredits called - updating credits...')
    try {

      const response = await axios.get(`${API_BASE_URL}/api/v3/account/credits`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data?.success && response.data?.data?.credits !== undefined) {
        const newCredits = response.data.data.credits
        
        // Update user state
        setUser(prev => prev ? { ...prev, credits: newCredits } : null)
        
        // Push to sidebar display via registered callback
        if (displayCreditsCallbackRef.current) {
          displayCreditsCallbackRef.current(newCredits)
        }
        
        // Update localStorage
        const storedUserData = localStorage.getItem('user')
        if (storedUserData) {
          const userData = JSON.parse(storedUserData)
          userData.creditBalance = newCredits
          localStorage.setItem('user', JSON.stringify(userData))
        }
      }
    } catch (error) {
      console.error('Failed to refresh credits:', error)
    }
  }, [])

  // Permission checking helpers
  const hasPermission = (permission: string): boolean => {
    if (!user) return false
    return user.permissions.includes(permission)
  }

  const hasPermissions = (permissions: string[], requireAll = false): boolean => {
    if (!user) return false
    if (requireAll) {
      return permissions.every(p => user.permissions.includes(p))
    }
    return permissions.some(p => user.permissions.includes(p))
  }

  const hasRole = (roles: string | string[]): boolean => {
    if (!user) return false
    const roleArray = Array.isArray(roles) ? roles : [roles]
    return roleArray.includes(user.roleCode)
  }

  const checkAccess = (config: {
    permissions?: string[]
    permissionsAll?: string[]
    roles?: string[]
  }): boolean => {
    if (!user) return false

    // Check roles
    if (config.roles && !hasRole(config.roles)) {
      return false
    }

    // Check permissions (OR logic)
    if (config.permissions && !hasPermissions(config.permissions, false)) {
      return false
    }

    // Check permissions (AND logic)
    if (config.permissionsAll && !hasPermissions(config.permissionsAll, true)) {
      return false
    }

    return true
  }

  // Register callback for updating display credits in sidebar
  const registerDisplayCreditsCallback = (callback: (credits: number) => void) => {
    displayCreditsCallbackRef.current = callback
  }

  // Update display credits in sidebar without updating user state
  const updateDisplayCredits = (credits: number) => {
    if (displayCreditsCallbackRef.current) {
      displayCreditsCallbackRef.current(credits)
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      refreshUser,
      refreshCredits,
      registerDisplayCreditsCallback,
      updateDisplayCredits,
      hasPermission,
      hasPermissions,
      hasRole,
      checkAccess
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
